/*
 * <pheres-bag> — the slide-out bag.
 *
 * Every add-to-bag form on the site is a real form that posts to /cart/add, so a
 * failed fetch or no JavaScript still ends with the piece in the bag. This listens
 * for those submits at the document, so the product page, the quick-buy on the
 * cards and anything added later all come through one door.
 *
 * Fragments come from the Section Rendering API, the same snippet the header paints
 * at load, so there is no second template of the bag in JavaScript.
 */
(() => {
  // Without a native modal there is no drawer to open. Doing nothing leaves every form
  // posting to /cart and the header's bag link going there too, which all still works.
  if (customElements.get('pheres-bag') || !window.HTMLDialogElement?.prototype.showModal) return;

  const SECTION = 'pheres-bag';
  const CLOSE_MS = 460;
  const REQUEST_TIMEOUT_MS = 15000;
  const ANNOUNCE_DELAY_MS = 150;
  const WARM_THUMBNAILS_MS = 2500;
  const SWIPE_START_PX = 10;
  const SWIPE_CLOSE_RATIO = 0.28;
  const SWIPE_CLOSE_SPEED = 0.55; // px per ms

  const isReduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  class PheresBag extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.statusEl = this.querySelector('[data-bag-status]');
      this.noticeEl = this.querySelector('[data-bag-notice]');
      this.queue = Promise.resolve();
      this.submitting = new WeakSet();
      this.controller = new AbortController();
      const opts = { signal: this.controller.signal };

      document.addEventListener('submit', (e) => this.onSubmit(e), opts);
      document.addEventListener('click', (e) => this.onDocumentClick(e), opts);
      this.dialog.addEventListener('click', (e) => this.onDialogClick(e), opts);
      this.dialog.addEventListener('change', (e) => this.onChange(e), opts);
      this.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.close(); }, opts);
      // A page restored from the back-forward cache shows a bag from before the
      // visit to checkout. Re-read it rather than show a stale count.
      window.addEventListener('pageshow', (e) => { if (e.persisted) this.refresh().catch(() => {}); }, opts);
      this.initSwipe(opts);
      // Lazy, so a bag nobody has opened never competes with the page it sits on, then
      // fetched anyway so the thumbnails are there the first time it slides in.
      this.warmTimer = setTimeout(() => {
        this.dialog.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      }, WARM_THUMBNAILS_MS);
    }

    disconnectedCallback() {
      this.controller.abort();
      clearTimeout(this.closeTimer);
      clearTimeout(this.warmTimer);
      clearTimeout(this.announceTimer);
    }

    // ---- open and close ----------------------------------------------------

    open() {
      clearTimeout(this.closeTimer);
      if (!this.dialog.open) this.dialog.showModal();
      document.documentElement.setAttribute('scroll-lock', '');
      // The panel has only just become display:flex. Force a layout so the slide
      // has a start position to transition from.
      void this.dialog.offsetWidth;
      this.dialog.classList.add('is-open');
    }

    close() {
      if (!this.dialog.open) return;
      this.dialog.style.transform = '';
      this.dialog.classList.remove('is-open');
      const finish = () => {
        if (this.dialog.open) this.dialog.close();
        document.documentElement.removeAttribute('scroll-lock');
      };
      clearTimeout(this.closeTimer);
      if (isReduced()) finish();
      else this.closeTimer = setTimeout(finish, CLOSE_MS);
    }

    // ---- talking to the cart ----------------------------------------------

    // Cart writes run one at a time and in order. Overlapping ones can overwrite each
    // other's lines, and answers arriving out of order would paint a stale bag.
    enqueue(task) {
      this.queue = this.queue.then(task, task);
      return this.queue;
    }

    // A request that stalls must not leave a button saying "Adding" for good.
    async request(url, init) {
      const timeout = new AbortController();
      const timer = setTimeout(() => timeout.abort(), REQUEST_TIMEOUT_MS);
      try {
        const res = await fetch(url, { ...init, signal: timeout.signal });
        const data = await res.json().catch(() => ({}));
        return { res, data };
      } finally {
        clearTimeout(timer);
      }
    }

    async post(url, payload) {
      const isForm = payload instanceof FormData;
      if (isForm) {
        payload.append('sections', SECTION);
        payload.append('sections_url', location.pathname);
      }
      const { res, data } = await this.request(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          ...(isForm ? {} : { 'Content-Type': 'application/json' }),
        },
        body: isForm
          ? payload
          : JSON.stringify({ ...payload, sections: SECTION, sections_url: location.pathname }),
      });
      if (!res.ok) {
        const err = new Error(data.description || data.message || '');
        err.status = res.status;
        throw err;
      }
      return data;
    }

    /** Adds a FormData, or `{ items: [...] }` for the stack builder, then slides open. */
    add(payload, options) {
      return this.enqueue(() => this.addNow(payload, options));
    }

    async addNow(payload, { form } = {}) {
      this.setNotice('');
      let data;
      try {
        data = await this.post(this.dataset.add, payload);
      } catch (err) {
        this.resetBuyButtons();
        // No status means the request never completed. The form still knows how to
        // do this the old way, and a buyer ending up on /cart beats a dead button.
        if (form && err.status === undefined) {
          form.submit();
          return null;
        }
        this.setNotice(this.messageFor(err));
        this.open();
        throw err;
      }

      // The piece is in the bag from here on, so nothing below may post it again.
      if (!this.paint(data.sections?.[SECTION])) await this.refresh().catch(() => false);
      (data.items || [data]).forEach((item) => this.lineFor(item.key)?.classList.add('is-new'));
      this.resetBuyButtons();
      // A card's size rail closes behind the add. Its summary takes focus first, so the
      // modal has somewhere real to hand focus back to when it closes.
      const rail = form?.closest('details');
      rail?.querySelector('summary')?.focus({ preventScroll: true });
      rail?.removeAttribute('open');
      document.dispatchEvent(new CustomEvent('pheres:bag:added', { detail: data }));
      this.open();
      this.keepFocus();
      this.announce(this.dataset.lAdded);
      return data;
    }

    change(key, quantity, options) {
      return this.enqueue(() => this.changeNow(key, quantity, options));
    }

    async changeNow(key, quantity, { line, focusId } = {}) {
      line?.classList.add('is-busy');
      this.setNotice('');
      try {
        const data = await this.post(this.dataset.change, { id: key, quantity });
        if (!this.paint(data.sections?.[SECTION])) await this.refresh();
        this.focusAfterChange(focusId);
        this.announce(this.dataset.lUpdated);
      } catch (err) {
        line?.classList.remove('is-busy', 'is-leaving');
        line?.style.removeProperty('height');
        this.setNotice(this.messageFor(err));
      }
    }

    async refresh() {
      const { res, data } = await this.request(`${location.pathname}?sections=${SECTION}`, {
        headers: { Accept: 'application/json' },
      });
      return res.ok ? this.paint(data[SECTION]) : false;
    }

    // Shopify's own reason is worth showing, such as a stock limit. A browser's reason,
    // "Failed to fetch" or "Load failed", is not.
    messageFor(err) {
      return (err.status && err.message) || this.dataset.lError;
    }

    // ---- painting ----------------------------------------------------------

    paint(html) {
      if (!html) return false;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const scroll = doc.querySelector('.bag__scroll');
      const totals = doc.querySelector('.bag__totals');
      if (!scroll || !totals) return false;

      const current = this.dialog.querySelector('.bag__scroll');
      const top = current.scrollTop;
      const noteWasOpen = current.querySelector('.bag__note')?.open;
      current.replaceWith(scroll);
      this.dialog.querySelector('.bag__totals').replaceWith(totals);
      scroll.scrollTop = top;
      if (noteWasOpen) scroll.querySelector('.bag__note')?.setAttribute('open', '');

      const count = Number(scroll.dataset.count || 0);
      this.dialog.toggleAttribute('data-empty', count === 0);
      this.dialog.toggleAttribute('data-rings', scroll.dataset.rings === 'true');
      this.setCount(count);
      return true;
    }

    setCount(count) {
      document.querySelectorAll('[data-bag-open]').forEach((link) => {
        let badge = link.querySelector('.hd__count');
        if (count > 0 && !badge) {
          badge = document.createElement('span');
          badge.className = 'hd__count u-num';
          link.append(badge);
        }
        if (count > 0) badge.textContent = String(count);
        else badge?.remove();
      });
    }

    lineFor(key) {
      return this.querySelector(`.bag__line[data-key="${CSS.escape(key)}"]`);
    }

    // Cleared first so the same words twice are still announced twice, and written a
    // moment after opening because a live region inside a closed dialog is not listening.
    announce(text) {
      if (!this.statusEl) return;
      clearTimeout(this.announceTimer);
      this.statusEl.textContent = '';
      this.announceTimer = setTimeout(() => { this.statusEl.textContent = text || ''; }, ANNOUNCE_DELAY_MS);
    }

    // paint() replaces what may have had focus. Inside a modal that strands the buyer on
    // nothing, so it goes back to the title.
    keepFocus() {
      if (!this.dialog.contains(document.activeElement)) {
        this.querySelector('#bag-title')?.focus({ preventScroll: true });
      }
    }

    // The button that was pressed comes back, unless it can no longer be pressed: minus
    // at one, plus at five. Then its partner, then the close button.
    focusAfterChange(focusId) {
      const pressed = focusId && this.querySelector(`[data-focus-id="${CSS.escape(focusId)}"]`);
      const next = [
        pressed,
        pressed?.closest('.bag__stepper')?.querySelector('[data-bag-step]:not([disabled])'),
        this.querySelector('[data-bag-close]'),
      ].find((el) => el && !el.disabled);
      next?.focus({ preventScroll: true });
    }

    setNotice(text) {
      if (!this.noticeEl) return;
      this.noticeEl.textContent = text;
      this.noticeEl.hidden = !text;
    }

    // The product page swaps its button to "Adding" and relied on the page leaving
    // to put it back. The page no longer leaves. Only a button still saying "Adding" is
    // touched: an add from elsewhere must not relabel one still asking for a size.
    resetBuyButtons() {
      document.querySelectorAll('[data-l-adding]').forEach((button) => {
        if (button.textContent.trim() === button.dataset.lAdding) button.textContent = button.dataset.lAdd;
      });
    }

    // ---- events ------------------------------------------------------------

    onSubmit(e) {
      const form = e.target;
      if (e.defaultPrevented || !(form instanceof HTMLFormElement)) return;
      if (!new URL(form.action, location.href).pathname.endsWith('/cart/add')) return;
      e.preventDefault();
      // A second tap on a button still on its way is one purchase, not two.
      if (this.submitting.has(form)) return;
      this.submitting.add(form);
      this.add(new FormData(form), { form })
        .catch(() => {})
        .finally(() => this.submitting.delete(form));
    }

    onDocumentClick(e) {
      const opener = e.target.closest?.('[data-bag-open]');
      if (!opener || e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
      e.preventDefault();
      this.open();
    }

    onDialogClick(e) {
      const target = e.target.closest('[data-bag-close], [data-bag-step], [data-bag-remove], [data-bag-add]');

      if (!target) {
        // Clicks on ::backdrop report the dialog itself, with coordinates off the panel.
        const box = this.dialog.getBoundingClientRect();
        if (e.target === this.dialog && e.clientX < box.left) this.close();
        return;
      }

      if (target.matches('[data-bag-close]')) {
        this.close();
        return;
      }

      const line = target.closest('.bag__line');

      if (target.matches('[data-bag-step]')) {
        this.change(line.dataset.key, Number(target.dataset.qty), {
          line,
          focusId: Number(target.dataset.qty) > 0 ? target.dataset.focusId : null,
        });
        return;
      }

      if (target.matches('[data-bag-remove]')) {
        e.preventDefault();
        line.style.height = `${line.offsetHeight}px`;
        void line.offsetHeight;
        line.classList.add('is-leaving');
        this.change(line.dataset.key, 0, { line });
        return;
      }

      // One tap, in the size already in the bag.
      if (target.getAttribute('aria-busy') === 'true') return;
      target.setAttribute('aria-busy', 'true');
      this.add({
        items: [{
          id: Number(target.dataset.variant),
          quantity: 1,
          properties: { 'Ring size': target.dataset.size },
        }],
      }).catch(() => target.removeAttribute('aria-busy'));
    }

    onChange(e) {
      if (e.target.name !== 'note') return;
      this.post(this.dataset.update, { note: e.target.value }).catch(() => {
        this.setNotice(this.dataset.lError);
      });
    }

    // ---- swipe to dismiss --------------------------------------------------

    initSwipe(opts) {
      let startX = 0;
      let startY = 0;
      let startAt = 0;
      let dx = 0;
      let dragging = false;
      let abandoned = false;
      const panel = this.dialog;

      panel.addEventListener('touchstart', (e) => {
        const touch = e.touches[0];
        startX = touch.clientX;
        startY = touch.clientY;
        startAt = e.timeStamp;
        dx = 0;
        dragging = false;
        abandoned = false;
      }, { ...opts, passive: true });

      panel.addEventListener('touchmove', (e) => {
        if (abandoned) return;
        const touch = e.touches[0];
        dx = Math.max(0, touch.clientX - startX);
        if (!dragging) {
          const dy = Math.abs(touch.clientY - startY);
          if (dy > SWIPE_START_PX && dy > dx) { abandoned = true; return; }
          if (dx < SWIPE_START_PX) return;
          dragging = true;
          panel.style.transition = 'none';
        }
        panel.style.transform = `translateX(${dx}px)`;
      }, { ...opts, passive: true });

      const end = (e) => {
        if (!dragging) return;
        dragging = false;
        panel.style.transition = '';
        const speed = dx / Math.max(1, e.timeStamp - startAt);
        if (dx > panel.offsetWidth * SWIPE_CLOSE_RATIO || speed > SWIPE_CLOSE_SPEED) this.close();
        else panel.style.transform = '';
      };
      panel.addEventListener('touchend', end, opts);
      panel.addEventListener('touchcancel', end, opts);
    }
  }

  customElements.define('pheres-bag', PheresBag);
})();
